'use client'

import { useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { zodResolver } from '@hookform/resolvers/zod'
import { Building2, Mail, Mountain } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'

import type { QuotePageContent } from '@/lib/content/defaults/quote'
import type { Suburb } from '@/lib/content/defaults/site-content'
import type { ContextResponseData } from '@/lib/context-response'
import { leadValue } from '@/lib/lead-value'
import { localiseResolver } from '@/lib/schemas/localise-resolver'
import { PHONE_MESSAGE } from '@/lib/schemas/phone'
import {
  commercialServiceOptions,
  quoteSchema,
  residentialServiceOptions,
  type QuoteFormData,
} from '@/lib/schemas/quote'
import { commercialServiceLabels, residentialServiceLabels } from '@/lib/schemas/quote-labels'
import { useCsrf } from '@/hooks/use-csrf'
import { Button } from '@/components/ui/button'
import { AltchaWidget, useAltcha } from '@/components/altcha-widget'
import { ContextResponse } from '@/components/context-response'
import { CsrfRetry } from '@/components/csrf-retry'
import { FieldError, FieldInput, FieldLabel } from '@/components/form-field'
import { HalloweenGhostHands, HalloweenTrinket } from '@/components/halloween'
import { HighlightedText } from '@/components/highlighted-text'
import { JobAddressField } from '@/components/job-address-field'
import { LucideIcon } from '@/components/lucide-icon'
import { MachineMobilisationHint } from '@/components/machine-mobilisation-hint'
import { MediaImage as Image } from '@/components/media-image'
import { PhoneLink } from '@/components/phone-link'
import { PunkHighlight } from '@/components/punk-highlight'
import { ScrollReveal } from '@/components/scroll-reveal'
import { ServiceAreaCard } from '@/components/service-area-card'
import { Sparkles } from '@/components/sparkles'
import { TopoFlow } from '@/components/topo-flow'
import { TrustStrip } from '@/components/trust-strip'
import { VideoBackground } from '@/components/video-background'

type FormStatus = 'idle' | 'submitting' | 'success' | 'error'
const processPools = ['excavation', 'drainage', 'landscaping'] as const

/**
 * Roughly two thirds of people who touch the form never submit. Without this
 * the failed attempts are indistinguishable from someone wandering off, so a
 * broken captcha or a rejected phone number would look like disinterest.
 */
function trackFormError(reason: string) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  window.gtag('event', 'form_error', {
    form_name: 'quote_request',
    form_id: 'quote',
    error_reason: reason,
  })
}

type QuoteClientProps = {
  content: Omit<QuotePageContent, 'seo'>
  drivewayPrice: number
  drivewayPriceLabel: string
  drivewayFullPriceLabel: string
  drivewaySpecialActive: boolean
  suburbs: Suburb[]
  salesEmail: string
}

export default function QuoteClient({
  content,
  drivewayPrice,
  drivewayPriceLabel,
  drivewayFullPriceLabel,
  drivewaySpecialActive,
  suburbs,
  salesEmail,
}: QuoteClientProps) {
  const { token: csrfToken } = useCsrf()
  const captcha = useAltcha()
  const [website, setWebsite] = useState('')
  const [status, setStatus] = useState<FormStatus>('idle')
  const [response, setResponse] = useState<ContextResponseData | null>(null)
  const [formStarted, setFormStarted] = useState(false)
  const [enquiryType, setEnquiryType] = useState<'residential' | 'commercial'>('residential')
  const inFlight = useRef(false)
  const v = content.form.validation
  const resolver = useMemo(
    () =>
      localiseResolver(zodResolver(quoteSchema), {
        'Name is required.': v.nameRequired,
        'Name is too long.': v.nameTooLong,
        'Phone number is required.': v.phoneRequired,
        'Phone number is too long.': v.phoneTooLong,
        [PHONE_MESSAGE]: v.phoneInvalid,
        'Postcode is required.': v.postcodeRequired,
        'Postcode is too long.': v.postcodeTooLong,
        'Job address is too long.': v.jobAddressTooLong,
        'Description is too long.': v.descriptionTooLong,
        'Please enter a valid email address.': v.emailInvalid,
        'Email is required for commercial enquiries.': v.emailRequiredCommercial,
        'Please select a service.': v.serviceRequired,
        'Company name is too long.': v.companyNameTooLong,
        'Please select a timeline.': v.timelineRequired,
      }),
    [v]
  )

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<QuoteFormData>({
    resolver,
    mode: 'onTouched',
    shouldFocusError: true,
    delayError: 300,
    defaultValues: {
      enquiryType: 'residential',
      name: '',
      phone: '',
      postcode: '',
      service: undefined,
      description: '',
      email: '',
      jobAddress: '',
    },
  })

  const jobAddressValue = useWatch({ control, name: 'jobAddress' }) ?? ''
  const postcodeValue = useWatch({ control, name: 'postcode' })
  const selectedService = useWatch({ control, name: 'service' })
  const postcodeStr = postcodeValue ?? ''
  const servicePool =
    selectedService === 'drainage'
      ? 'drainage'
      : selectedService === 'landscaping'
        ? 'landscaping'
        : selectedService === 'retaining' || selectedService === 'rock-walls'
          ? 'masonry'
          : 'excavation'

  const onSubmit = async (data: QuoteFormData) => {
    if (inFlight.current || !csrfToken) return
    inFlight.current = true
    try {
      setStatus('submitting')
      setResponse(null)

      const payload = await captcha.solve()
      if (!payload) {
        setStatus('error')
        setResponse({ severity: 'danger', text: content.form.labels.errorMessage })
        trackFormError('captcha')
        return
      }

      try {
        const res = await fetch('/api/quote', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': csrfToken,
          },
          body: JSON.stringify({
            ...data,
            captchaPayload: payload,
            website,
          }),
        })

        const result = (await res.json().catch(() => ({}))) as Partial<ContextResponseData> & {
          error?: string
        }

        if (res.ok) {
          setStatus('success')
          setResponse({
            severity: result.severity ?? 'success',
            text: result.text ?? content.form.labels.successMessage,
          })
          reset()
          captcha.reset()

          // GA4 events
          if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', 'generate_lead', {
              currency: 'AUD',
              value: leadValue(data.service, drivewayPrice),
              event_category: 'engagement',
              event_label: data.service,
            })
            window.gtag('event', 'form_submit', {
              form_name: 'quote_request',
              form_id: 'quote',
              service_type: data.service,
              postcode: data.postcode,
            })
          }
        } else {
          setStatus('error')
          setResponse({
            severity: result.severity ?? 'danger',
            text: result.text ?? result.error ?? content.form.labels.errorMessage,
          })
          captcha.reset()
          trackFormError(`http_${res.status}`)
        }
      } catch {
        setStatus('error')
        setResponse({
          severity: 'danger',
          text: content.form.labels.networkErrorMessage,
        })
        captcha.reset()
        trackFormError('network')
      }
    } finally {
      inFlight.current = false
    }
  }

  return (
    <>
      {/* Hero - DARK */}
      <section
        id="hero"
        className="texture-stone-dark grain-dark stage-light-dark torn-bottom relative overflow-hidden bg-[#1a0e20] pt-6 pb-10 sm:pt-10 sm:pb-12"
        data-tear="6"
      >
        <HalloweenTrinket
          pool="rock-monsters"
          character="ghost"
          className="spoopy-hero pointer-events-none absolute hidden xl:block"
        />
        <TopoFlow
          seed={7}
          lineCount={14}
          scale={0.002}
          speed={0.15}
          strokeColour="rgba(149,248,98,0.08)"
        />
        <VideoBackground
          src="/videos/quote-ranges.mp4"
          poster="/images/posters/quote-ranges.webp"
          priority
          className="opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a0e20] via-[#1a0e20]/80 to-transparent" />
        <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
          <ScrollReveal direction="up">
            <h1 className="leading-tight text-white">
              <span className="font-marker block text-2xl text-white/70 sm:text-3xl lg:text-4xl">
                <HighlightedText text={content.hero.eyebrow} highlightColour="purple" />
              </span>{' '}
              <span className="font-display mt-2 block text-5xl font-bold sm:text-6xl lg:text-7xl">
                <HighlightedText text={content.hero.heading} highlightColour="green" />
              </span>
            </h1>
          </ScrollReveal>
          <ScrollReveal direction="up" delay={100}>
            <p className="mt-4 max-w-2xl text-lg text-white/60">{content.hero.body}</p>
          </ScrollReveal>
          <ScrollReveal direction="up" delay={200}>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:gap-4">
              <Button asChild size="lg" className="relative h-14 px-10 text-xl">
                <a href={content.hero.primaryCtaHref}>
                  <LucideIcon name={content.hero.primaryCtaIconKey} className="mr-1 size-5" />
                  {content.hero.primaryCtaLabel}
                  <Sparkles seed={10} />
                </a>
              </Button>
              <Button
                asChild
                variant="ghost"
                size="lg"
                className="border-brand-chartreuse/40 bg-brand-chartreuse/10 text-brand-chartreuse-vivid hover:border-brand-chartreuse hover:bg-brand-chartreuse/25 h-12 border px-8 text-lg font-bold transition-all hover:text-white active:scale-95"
              >
                <PhoneLink showPrefix />
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Form section */}
      <section
        id="form"
        className="texture-stone-light grain-light torn-top torn-bottom relative isolate overflow-hidden bg-white pt-16 pb-16 text-[#1a0e20]"
        data-tear="4"
      >
        <HalloweenGhostHands variant={2} />
        <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid gap-6 lg:grid-cols-12 lg:gap-16">
            {/* Form column */}
            <div className="flex lg:col-span-7">
              <ScrollReveal direction="up" className="flex w-full">
                <div className="relative flex-1 rounded-sm border-2 border-[#c4b0d4] bg-[#f8f6fa] p-6 shadow-md sm:p-10">
                  {/* Top accent bar */}
                  <div className="from-brand-green via-brand-chartreuse to-brand-green absolute inset-x-0 top-0 h-2 rounded-t-sm bg-gradient-to-r" />

                  <div className="relative mb-8 pr-24">
                    <h2 className="font-heading flex items-center gap-3 text-3xl font-bold sm:text-4xl">
                      <LucideIcon
                        name={content.form.iconKey}
                        className="text-brand-green size-7 shrink-0 sm:size-8"
                      />
                      <span className="min-w-0">{content.form.heading}</span>
                    </h2>
                    <p className="mt-2 text-base text-[#555]">{content.form.intro}</p>
                    <HalloweenTrinket
                      pool="rock-monsters"
                      character="mummy"
                      className="absolute top-0 right-0 size-20"
                    />
                  </div>

                  <form
                    onSubmit={(e) =>
                      handleSubmit(onSubmit, (invalid) =>
                        trackFormError(`invalid:${Object.keys(invalid).sort().join(',')}`)
                      )(e)
                    }
                    className="space-y-5"
                    noValidate
                    onFocus={() => {
                      if (!formStarted) {
                        setFormStarted(true)
                        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                          window.gtag('event', 'form_start', {
                            form_name: 'quote_request',
                            form_id: 'quote',
                          })
                        }
                      }
                    }}
                  >
                    {/* Enquiry type toggle */}
                    <div className="flex gap-2 rounded-sm border border-[#d8cfe0] bg-[#f8f5fa] p-1">
                      <button
                        type="button"
                        onClick={() => {
                          setEnquiryType('residential')
                          setValue('enquiryType', 'residential')
                          setValue('service', '' as any)
                        }}
                        className={`min-h-11 flex-1 rounded-sm px-4 py-2.5 text-sm font-semibold transition-colors ${
                          enquiryType === 'residential'
                            ? 'bg-brand-purple text-white shadow-sm'
                            : 'text-[#555] hover:text-[#1a0e20]'
                        }`}
                      >
                        <Mountain className="mr-1.5 inline-block size-4" aria-hidden="true" />
                        {content.form.labels.residentialToggle}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEnquiryType('commercial')
                          setValue('enquiryType', 'commercial')
                          setValue('service', '' as any)
                        }}
                        className={`min-h-11 flex-1 rounded-sm px-4 py-2.5 text-sm font-semibold transition-colors ${
                          enquiryType === 'commercial'
                            ? 'bg-brand-green text-white shadow-sm'
                            : 'text-[#555] hover:text-[#1a0e20]'
                        }`}
                      >
                        <Building2 className="mr-1.5 inline-block size-4" aria-hidden="true" />
                        {content.form.labels.commercialToggle}
                      </button>
                    </div>
                    {enquiryType === 'commercial' && (
                      <div className="rounded-sm border border-[#d8cfe0] bg-[#f0ebf5] px-4 py-3">
                        <p className="text-sm text-[#333]">
                          {content.form.commercialUpsellPrefix}
                          <Link
                            href={content.form.commercialUpsellLinkHref}
                            className="text-brand-purple font-semibold underline"
                          >
                            {content.form.commercialUpsellLinkLabel}
                          </Link>
                          {content.form.commercialUpsellSuffix}
                        </p>
                      </div>
                    )}

                    {/* Name + Phone row */}
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <FieldLabel htmlFor="name">{content.form.labels.nameLabel}</FieldLabel>
                        <FieldInput
                          type="text"
                          id="name"
                          autoComplete="name"
                          autoCapitalize="words"
                          enterKeyHint="next"
                          aria-invalid={!!errors.name}
                          aria-describedby={errors.name ? 'name-error' : undefined}
                          placeholder={content.form.labels.namePlaceholder}
                          disabled={status === 'submitting'}
                          {...register('name')}
                        />
                        <FieldError id="name-error" message={errors.name?.message} />
                      </div>

                      <div>
                        <FieldLabel htmlFor="phone">{content.form.labels.phoneLabel}</FieldLabel>
                        <FieldInput
                          type="tel"
                          id="phone"
                          inputMode="tel"
                          autoComplete="tel"
                          autoCorrect="off"
                          spellCheck={false}
                          enterKeyHint="next"
                          aria-invalid={!!errors.phone}
                          aria-describedby={errors.phone ? 'phone-error' : undefined}
                          placeholder={content.form.labels.phonePlaceholder}
                          disabled={status === 'submitting'}
                          {...register('phone')}
                        />
                        <FieldError id="phone-error" message={errors.phone?.message} />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <FieldLabel htmlFor="email">
                        {content.form.labels.emailLabel}
                        {enquiryType === 'commercial' && (
                          <span className="ml-1 text-red-400">*</span>
                        )}
                      </FieldLabel>
                      <FieldInput
                        type="email"
                        id="email"
                        autoComplete="email"
                        enterKeyHint="next"
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                        placeholder={
                          enquiryType === 'commercial'
                            ? content.form.labels.emailPlaceholderCommercial
                            : content.form.labels.emailPlaceholderResidential
                        }
                        disabled={status === 'submitting'}
                        {...register('email')}
                      />
                      <FieldError id="email-error" message={errors.email?.message} />
                    </div>

                    <JobAddressField
                      id="jobAddress"
                      value={jobAddressValue}
                      disabled={status === 'submitting'}
                      onTyped={(value) => {
                        setValue('jobAddress', value)
                        setValue('placeId', undefined)
                        setValue('lat', undefined)
                        setValue('lng', undefined)
                      }}
                      onPicked={(picked) => {
                        setValue('jobAddress', picked.jobAddress)
                        setValue('placeId', picked.placeId)
                        setValue('lat', picked.lat)
                        setValue('lng', picked.lng)
                        if (picked.postcode) {
                          setValue('postcode', picked.postcode, { shouldValidate: true })
                        }
                      }}
                    />

                    {/* Suburb + Service row */}
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <FieldLabel htmlFor="postcode">
                          {content.form.labels.postcodeLabel}
                        </FieldLabel>
                        <FieldInput
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          id="postcode"
                          autoComplete="postal-code"
                          autoCorrect="off"
                          spellCheck={false}
                          enterKeyHint="next"
                          aria-invalid={!!errors.postcode}
                          aria-describedby={errors.postcode ? 'postcode-error' : undefined}
                          placeholder={content.form.labels.postcodePlaceholder}
                          disabled={status === 'submitting'}
                          {...register('postcode')}
                        />
                        <FieldError id="postcode-error" message={errors.postcode?.message} />
                      </div>

                      <div>
                        <FieldLabel htmlFor="service">
                          {content.form.labels.serviceLabel}
                        </FieldLabel>
                        <select
                          id="service"
                          aria-invalid={!!errors.service}
                          aria-describedby={errors.service ? 'service-error' : undefined}
                          className="input-rock focus:border-brand-purple focus:ring-brand-purple w-full rounded-sm border border-[#d8cfe0] bg-[#f8f5fa] px-4 py-3 text-[#1a1a1a] focus:ring-1 focus:outline-none"
                          defaultValue=""
                          disabled={status === 'submitting'}
                          {...register('service')}
                        >
                          <option value="" disabled>
                            {content.form.labels.servicePlaceholder}
                          </option>
                          {enquiryType === 'residential'
                            ? residentialServiceOptions.map((value) => (
                                <option key={value} value={value}>
                                  {residentialServiceLabels[value]}
                                </option>
                              ))
                            : commercialServiceOptions.map((value) => (
                                <option key={value} value={value}>
                                  {commercialServiceLabels[value]}
                                </option>
                              ))}
                        </select>
                        <FieldError id="service-error" message={errors.service?.message} />
                        <div className="mt-2 flex justify-end">
                          <HalloweenTrinket pool={servicePool} initial={1} className="size-20" />
                        </div>
                      </div>
                    </div>

                    <MachineMobilisationHint postcode={postcodeStr} hasError={!!errors.postcode} />

                    {enquiryType === 'commercial' && (
                      <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                          <label
                            htmlFor="companyName"
                            className="font-heading text-brand-purple mb-2 block text-base font-semibold tracking-wider uppercase"
                          >
                            {content.form.labels.companyNameLabel}
                          </label>
                          <input
                            type="text"
                            id="companyName"
                            autoComplete="organization"
                            enterKeyHint="next"
                            className="input-rock focus:border-brand-purple focus:ring-brand-purple w-full rounded-sm border border-[#d8cfe0] bg-[#f8f5fa] px-4 py-3 text-[#1a1a1a] placeholder:text-[#aaa] focus:ring-1 focus:outline-none"
                            placeholder={content.form.labels.companyNamePlaceholder}
                            disabled={status === 'submitting'}
                            {...register('companyName' as any)}
                          />
                        </div>

                        <div>
                          <label
                            htmlFor="timeline"
                            className="font-heading text-brand-purple mb-2 block text-base font-semibold tracking-wider uppercase"
                          >
                            {content.form.labels.timelineLabel}
                          </label>
                          <select
                            id="timeline"
                            className="input-rock focus:border-brand-purple focus:ring-brand-purple w-full rounded-sm border border-[#d8cfe0] bg-[#f8f5fa] px-4 py-3 text-[#1a1a1a] focus:ring-1 focus:outline-none"
                            defaultValue=""
                            disabled={status === 'submitting'}
                            {...register('timeline' as any)}
                          >
                            <option value="" disabled>
                              {content.form.labels.timelinePlaceholder}
                            </option>
                            <option value="asap">{content.form.labels.timelineAsap}</option>
                            <option value="1-3months">
                              {content.form.labels.timelineOneToThreeMonths}
                            </option>
                            <option value="3plus">
                              {content.form.labels.timelineThreePlusMonths}
                            </option>
                          </select>
                        </div>
                      </div>
                    )}

                    <div>
                      <FieldLabel htmlFor="description">
                        {content.form.labels.descriptionLabel}
                      </FieldLabel>
                      <textarea
                        id="description"
                        rows={4}
                        autoCapitalize="sentences"
                        enterKeyHint="done"
                        aria-invalid={!!errors.description}
                        aria-describedby={errors.description ? 'description-error' : undefined}
                        className="input-rock focus:border-brand-purple focus:ring-brand-purple w-full rounded-sm border border-[#d8cfe0] bg-[#f8f5fa] px-4 py-3 text-[#1a1a1a] placeholder:text-[#aaa] focus:ring-1 focus:outline-none"
                        placeholder={content.form.labels.descriptionPlaceholder}
                        disabled={status === 'submitting'}
                        {...register('description')}
                      />
                      <FieldError id="description-error" message={errors.description?.message} />
                    </div>

                    {/* Honeypot: visually hidden, must stay empty */}
                    <div
                      aria-hidden="true"
                      className="absolute left-[-9999px] h-0 w-0 overflow-hidden"
                    >
                      <label>
                        Website
                        <input
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          value={website}
                          onChange={(e) => setWebsite(e.target.value)}
                        />
                      </label>
                    </div>

                    <AltchaWidget captcha={captcha} />

                    {/* Status messages */}
                    {response && (status === 'success' || status === 'error') && (
                      <ContextResponse
                        severity={response.severity}
                        message={response.text}
                        size="lg"
                      />
                    )}

                    <CsrfRetry />

                    <Button
                      type="submit"
                      size="lg"
                      className="relative h-auto min-h-14 w-full px-6 py-3 text-xl tracking-wide sm:min-h-16 sm:w-auto sm:px-8 sm:text-2xl"
                      disabled={status === 'submitting' || !csrfToken}
                    >
                      {status === 'submitting' ? (
                        content.form.submittingLabel
                      ) : (
                        <>
                          <LucideIcon name={content.form.submitIconKey} className="mr-1 size-6" />
                          {content.form.submitLabel}
                        </>
                      )}
                    </Button>
                  </form>

                  <TrustStrip />
                </div>
              </ScrollReveal>
            </div>

            {/* Sidebar */}
            <div className="flex flex-col gap-6 lg:col-span-5">
              {/* Price sticker */}
              {enquiryType === 'residential' && (
                <ScrollReveal direction="right" delay={200}>
                  <div
                    className="sticker-animate bg-brand-chartreuse-vivid relative rounded-sm p-6 shadow-[4px_4px_0px_rgba(0,0,0,0.15)]"
                    style={{ transform: 'rotate(1deg)' }}
                  >
                    <Sparkles seed={15} colours={['#8a2585', '#e995e8', '#5b1b5a', '#00a134']} />
                    <Sparkles seed={21} colours={['#8a2585', '#e995e8', '#5b1b5a', '#00a134']} />
                    <p className="font-marker text-xl text-black sm:text-2xl">
                      {content.priceSticker.label}{' '}
                      {drivewaySpecialActive && (
                        <>
                          <span className="price-slash text-black/40">
                            {drivewayFullPriceLabel}
                          </span>{' '}
                        </>
                      )}
                      <PunkHighlight colour="purple" rotation="-2deg">
                        {drivewayPriceLabel}
                        {drivewaySpecialActive && '*'}
                      </PunkHighlight>
                    </p>
                    <p className="mt-1 text-xs text-black/50">{content.priceSticker.conditions}</p>
                    <p className="mt-2 text-base text-black/70">{content.priceSticker.body}</p>
                    <HalloweenTrinket
                      pool="masonry"
                      initial={1}
                      className="pointer-events-none absolute -top-5 -right-5 size-20"
                    />
                  </div>
                </ScrollReveal>
              )}

              {/* Process steps */}
              <ScrollReveal direction="left" delay={350}>
                <div className="relative rounded-sm border border-white/10 bg-[#1f1228]/80 p-6 backdrop-blur-sm sm:p-8">
                  <div className="from-brand-purple via-brand-purple-light to-brand-purple absolute inset-x-0 top-0 h-2 bg-gradient-to-r" />
                  <h3 className="font-heading flex items-center gap-2 text-2xl font-bold text-white sm:text-3xl">
                    <LucideIcon
                      name={content.process.iconKey}
                      className="text-brand-chartreuse-vivid size-6 shrink-0"
                    />
                    <span className="min-w-0">
                      <HighlightedText text={content.process.heading} highlightColour="green" />
                    </span>
                  </h3>
                  <ol className="mt-5 space-y-1">
                    {content.process.steps.map((step, i) => (
                      <li key={step.number} className="relative">
                        {i > 0 && <div className="divider-glow my-3 h-px" aria-hidden="true" />}
                        <div className="flex items-center gap-3">
                          <span className="font-display text-brand-chartreuse-vivid text-4xl leading-none font-bold sm:text-5xl">
                            {step.number}
                          </span>
                          <div className="min-w-0 flex-1 pr-12">
                            <span className="font-heading text-lg font-bold text-white sm:text-xl">
                              <HighlightedText text={step.title} highlightColour="chartreuse" />
                            </span>
                            <p className="mt-1 text-base text-white/60">
                              <HighlightedText text={step.body} highlightColour="chartreuse" />
                            </p>
                          </div>
                        </div>
                        <HalloweenTrinket
                          pool={processPools[i % processPools.length]}
                          initial={i}
                          className="pointer-events-none absolute top-1/2 -right-10 size-14 -translate-y-1/2"
                        />
                      </li>
                    ))}
                  </ol>
                </div>
              </ScrollReveal>

              {/* Phone CTA */}
              <ScrollReveal direction="left" delay={500}>
                <div className="relative rounded-sm border border-white/10 bg-[#1f1228]/80 p-6 backdrop-blur-sm sm:p-8">
                  <div className="from-brand-green via-brand-green-light to-brand-green absolute inset-x-0 top-0 h-2 bg-gradient-to-r" />
                  <h3 className="font-heading flex items-center gap-2 text-2xl font-bold text-white sm:text-3xl">
                    <LucideIcon
                      name={content.phoneCta.iconKey}
                      className="text-brand-green size-6 shrink-0"
                    />
                    <span className="min-w-0">{content.phoneCta.heading}</span>
                  </h3>
                  <p className="font-marker text-brand-chartreuse-vivid mt-2 text-lg">
                    {content.phoneCta.tagline}
                  </p>
                  <Button asChild size="lg" className="mt-4 h-11 px-6 text-xl">
                    <PhoneLink showPrefix />
                  </Button>
                  {salesEmail && (
                    <p className="mt-4 pr-20 text-base text-white/60">
                      {content.phoneCta.emailPrompt}{' '}
                      <a
                        href={`mailto:${salesEmail}`}
                        className="text-brand-chartreuse-vivid hover:text-brand-chartreuse inline-flex items-center gap-1.5 font-semibold break-all underline-offset-4 hover:underline"
                      >
                        <Mail className="size-4 shrink-0" aria-hidden="true" />
                        {salesEmail}
                      </a>
                    </p>
                  )}
                  <HalloweenTrinket
                    pool="friendly-hands"
                    initial={0}
                    className="pointer-events-none absolute -right-3 -bottom-5 size-20"
                  />
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Service area */}
      <section className="grain-dark bg-[#1a0e20] py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <ScrollReveal direction="up">
            <div className="relative">
              <ServiceAreaCard suburbs={suburbs} />
              <HalloweenTrinket
                pool="excavation"
                initial={2}
                className="spoopy-satellite pointer-events-none absolute -top-8 right-3 size-20"
              />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Bottom CTA - DARK */}
      <section
        id="cta"
        className="texture-stone-dark grain-dark stage-light-dark torn-top relative overflow-hidden bg-[#1a0e20] py-16"
      >
        <Image
          src="/images/access-road-forest.webp"
          alt=""
          fill
          className="object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a0e20] via-[#1a0e20]/70 to-[#1a0e20]/90" />
        <ScrollReveal direction="up">
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 md:flex md:items-center md:justify-between">
            <div>
              <h2 className="font-marker relative flex items-start gap-3 pr-20 text-4xl font-bold text-white sm:text-5xl">
                <LucideIcon
                  name={content.finalCta.iconKey}
                  className="text-brand-chartreuse-vivid size-10 shrink-0 sm:size-12"
                />
                <span>
                  <HighlightedText text={content.finalCta.heading} highlightColour="chartreuse" />
                </span>
                <HalloweenTrinket
                  pool="grave-tools"
                  initial={2}
                  className="pointer-events-none absolute -top-2 right-0 size-20"
                />
              </h2>
              <p className="mt-4 max-w-lg text-lg text-white/60">{content.finalCta.body}</p>
            </div>
            <div className="mt-8 flex flex-col items-start gap-3 md:mt-0 md:items-end">
              <Button
                asChild
                size="lg"
                className="bg-brand-chartreuse-vivid h-14 border border-[#b0ed54] px-10 text-xl font-bold text-[#1a0e20] shadow-[4px_4px_0px_rgba(0,0,0,0.35)] transition-all hover:bg-[#b0ed54] active:scale-95"
              >
                <PhoneLink showPrefix className="inline-flex items-center" />
              </Button>
              {salesEmail && (
                <Button
                  asChild
                  size="lg"
                  variant="ghost"
                  className="border-brand-chartreuse/40 bg-brand-chartreuse/10 text-brand-chartreuse-vivid hover:border-brand-chartreuse hover:bg-brand-chartreuse/25 h-auto min-h-12 max-w-full border px-4 py-2 text-sm font-bold transition-all hover:text-white active:scale-95 sm:px-8 sm:text-base"
                >
                  <a href={`mailto:${salesEmail}`} className="break-all">
                    <Mail className="mr-2 size-5 shrink-0" aria-hidden="true" />
                    {salesEmail}
                  </a>
                </Button>
              )}
            </div>
          </div>
        </ScrollReveal>
      </section>
    </>
  )
}
